import { HttpClient } from '@angular/common/http';
import {
  AfterContentInit,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BaseComponent } from '@macs-frontend/core';
import { RR } from '@macs-frontend/core/utils/fp-ts';
import { rx } from '@macs-frontend/core/utils/rx';
import { UUID, uuid4 } from '@macs-frontend/core/utils/uuid';
import { environment } from '@macs-frontend/env';
import { State } from '@macs-frontend/reducers';
import { DropdownApiService } from '@macs-frontend/web-macs/core/services/dropdown-api.service';
import { addQueryParamsToUrl } from '@macs-frontend/web-macs/core/utils/uri-utils';
import { APIResponse } from '@macs-frontend/web-macs/shared';
import { ErrorHelperService } from '@macs-frontend/web-macs/shared/services/error-helper.service';
import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  firstValueFrom,
  forkJoin,
  from,
  isObservable,
  Observable,
  of,
  Subject,
} from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  mapTo,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { DropdownTemplateDirective } from '../../directives/dropdown-template/dropdown-template.directive';

const cnt = 0;

export interface OptionsLoadEvent {
  options: any[];
  offset: number;
}

export interface DropdownOptionsGroup {
  title: string;
  options: any[];
}

export interface DropdownOptionsSource {
  url: string;
  title?: string;
  itemName?: string;
  queryParams?: { [key: string]: string | number };
  searchBy?: string;
  orderBy?: string;
}

interface MultipleValue {
  label: string;
  amount: number;
}

export interface AddGroupedOptionPayload {
  value: string;
  itemName: string;
}

export type DropdownLoadOptionById = (opts: {
  readonly id: string;
  readonly url: string;
}) => unknown;

export type DropdownMkOption = (opts: {
  value: string;
  url: string;
  payload: RR.ReadonlyRecord<string, unknown>;
  createOptionBy: string;
}) => unknown;

export type DropdownLoadOptionsByUrl = (opts: {
  readonly url: string;
  readonly limit: number;
  readonly orderBy: string;
  readonly params: RR.ReadonlyRecord<string, unknown>;
  readonly searchBy: string;
  readonly search: string;
  readonly page?: number;
  readonly shape?: RR.ReadonlyRecord<string, unknown>;
  readonly showApiErrors: boolean;
}) => Promise<readonly unknown[]>;

@Component({
  selector: 'md-dropdown',
  templateUrl: 'dropdown.component.html',
  styleUrls: ['./dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DropdownComponent),
      multi: true,
    },
  ],
})
export class DropdownComponent
  extends BaseComponent
  implements
    ControlValueAccessor,
    OnInit,
    OnChanges,
    AfterContentInit,
    OnDestroy,
    AfterViewInit
{
  get model() {
    return this.modelValue;
  }

  set model(value) {
    this.writeValue(value);
    this.onChangeCallback(value);
  }

  constructor(
    private dropdownApiService: DropdownApiService,
    private http: HttpClient,
    private errorHelperService: ErrorHelperService,
    readonly cdr: ChangeDetectorRef,
    readonly store$: Store<State>,
    private readonly elementRef: ElementRef,
  ) {
    super();

    this.loadChosenOptionSubject
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => this.checkAndLoadChosenOption());
  }

  @Input() debug = false;

  @Input() initialValue: any;

  @Input() emptyValue: any = null;

  /**
   * options array to show in dropdown
   */
  @Input() options: readonly any[] = [];

  /**
   * if dropdown option is object determines key to show as option label
   */
  @Input() labelKey?: string | number;

  /**
   * determines the way of getting label if dropdown option is object
   */
  // eslint-disable-next-line @typescript-eslint/ban-types
  @Input() labelCallback?: Function;

  /**
   * if dropdown option is object determines key to store as value
   * if not set will store whole option object as value
   */
  @Input() valueKey?: string;

  /**
   * if dropdown option is object and "valueKey" is not set
   * determines key to compare dropdown options
   */
  @Input() compareKey?: string;

  /**
   * If provided, description will be shown below the each option.
   */
  @Input() descriptionKey?: string;

  /**
   * determines the way of getting identification if dropdown option is object
   */
  @Input() idKey = (option: unknown): string => (option as any).uuid;

  /**
   * Show selected value even if it is not exists in options list
   */
  @Input() canUseModelAsOption?: boolean | null;

  /**
   * Clear model if input value is empty on blur.
   */
  @Input() canClearModelIfEmptyInput = true;

  /**
   * If true: the current value will be used as model on click on `create new ..` button.
   *          Could be used with `canAdd: true` and `canUseModelAsOption: true`.
   */
  @Input() canAddToModel?: boolean | null;

  /**
   * <input> placeholder
   */
  @Input() placeholder?: string;

  /**
   * if false <input> will be read only
   * if true allows dropdown options filtering by typing into the <input>
   * automatically sets to true if "url" or "autocomplete" options are set
   * see "autocomplete" and "url" options for more details
   */
  @Input() filter = false;

  /**
   * if false filters dropdown options locally
   * if true "find" event will be emitted for external filtering
   * automatically sets to true if "url" option is set
   * also see "url" option
   */
  @Input() autocomplete = false;

  /**
   * <input> autofocus
   */
  @Input() autofocus = false;

  /**
   * used in "+ create new.." sentence if canAdd = true
   */
  @Input() itemName = '';

  /**
   * if true "add new ..." row wil be shown in the dropdown
   */
  @Input() canAdd?:
    | boolean
    | null
    | readonly string[]
    | ((input?: string | null) => boolean) = false;

  /**
   * if true "add new ..." row wil be shown in the dropdown
   */
  @Input() canSelect: boolean | ((input?: string | null) => boolean) = true;

  /**
   * if true "add new ..." row will be shown even if there is fully matching
   * label in the dropdown options (allows duplicates)
   */
  @Input() canAddOnMatchLabel = false;

  @Input() existingLabels: any[] = [];

  @Input() canAddEmpty = true;

  @Input() disabledOnLoading = false;

  /**
   * DEPRECATED
   * if true cross icon will be shown for value clearing
   */
  @Input() canClear?: boolean | null = false;

  /**
   * classes to be appended to the <input>
   */
  @Input() inputClasses = 'input_sm';

  /**
   * classes to be appended to container <div>
   */
  @Input() containerClasses?: string;

  /**
   * classes to be appended to <div class="dropdown">
   */
  @Input() dropdownClasses?:
    | string
    | string[]
    | Set<string>
    | {
        [klass: string]: any;
      };

  @Input() dropdownStyles?: {
    [klass: string]: any;
  } | null;

  @Input() alwaysActive = false;

  /**
   * <input> size
   */
  // @ts-expect-error
  @Input() size: number;

  /**
   * enables multiple choice mode, value will be array
   * NOTE: many options will not work in this mode
   */
  @Input() multiple = false;

  /**
   * If true, labels will be joined with comma for multiple selections.
   * Otherwise, the label will be "Multiple (x)".
   */
  @Input() joinMultipleLables = false;

  /**
   * allows to show value as dropdown label (<input> value) if
   * corresponding dropdown option was not found
   */
  @Input() showInvalid = false;

  /**
   * <input> disabled
   */
  @Input() disabled = false;

  /**
   * <input> disabled
   */
  @Input() showArrowDown = true;

  /**
   * <input> width = autoWidth ? "auto" : "100%"
   */
  @Input() autoWidth = false;
  @Input() storeInputValueToModel = false;

  /*** OPTIONS BELOW CAN BE USED ONLY TOGETHER WITH "url" OPTION ***/

  /**
   * url or source collection for autoload dropdown options array
   */
  @Input() url?: string | DropdownOptionsSource[] | null;

  /**
   * working only when "canAdd" option is true
   * automatically creates dropdown option by sending POST request to "url"
   * doesn't work when "url" option is array of sources
   */
  @Input() canCreate = true;

  /**
   * automatically loads dropdown option by sending GET request to "url" appending
   * "uuid" of entity
   * working only when some entity "uuid" is used as value
   * doesn't work when "url" option is array of sources
   */
  @Input() canLoadChosenOption = true;

  /**
   * enables infiniteScroll for loading more dropdown options when scrolling
   * to the end of dropdown list by sending GET request to "url" appending
   * "page" query param
   * doesn't work when "url" option is array of sources
   */
  @Input() infiniteScroll = true;

  /**
   * "limit" query param for dropdown options loading
   */
  @Input() limit = 10;

  /**
   * see "filterOptionsFn" for more details
   * if after loading dropdown options by "infiniteScroll" and calling "filterOptionsFn"
   * there is less than "autoLoadMoreThreshold" dropdown options, more dropdown options
   * will be loaded automatically
   */
  @Input() autoLoadMoreThreshold = this.limit - 1;

  /**
   * determines field name for dropdown options searching
   * when "url" option is array of sources and source hasn't its own
   * "searchBy" this option will be used
   */
  @Input() searchBy = 'search';

  /**
   * see "canCreate" option for more details
   * determines field that will be sent when create new dropdown option
   */
  @Input() createOptionBy = 'name';

  /**
   * "orderBy" query param for dropdown options loading
   * when "url" option is array of sources and source hasn't its own
   * "orderBy" this option will be used
   */
  @Input() orderBy = 'name';

  /**
   * if string determines key of dropdown options that will be used for grouping
   * supports nested keys, for example "prop.nestedProp"
   * if true dropdown options will be grouped by each source even if there is only
   * one source set
   */
  // @ts-expect-error
  @Input() groupBy: string | true;

  /**
   * determines key of dropdown options to show as groups titles
   * supports nested keys, for example "prop.nestedProp"
   * when "url" option is array of sources title from each source object
   * will be used instead of this option
   */
  @Input() groupByTitleKey?: string;

  @Input() groupByTitleCallback?: (option: any) => string;

  /**
   * groups title to show when title was not found by "groupByTitleKey"
   */
  @Input() emptyGroupTitle = 'No group';

  /**
   * external filter function that will be called after each loading of
   * dropdown options
   *
   * @param options - loaded dropdown options
   * @param getOptionsParams - see "getOptionsParams" option
   * @param filterPayload - see "filterOptionsPayload" option
   * @return filtered dropdown options array or Observable that emits
   * filtered dropdown options
   */
  @Input()
  filterOptionsFn?: (
    options: any[],
    getOptionsParams: any,
    filterPayload: any,
    dropdown: DropdownComponent,
  ) => any[] | Observable<any[]>;

  /**
   * see "canCreate" option for more details
   * object that will be sent when create new dropdown option
   */
  @Input() createOptionPayload: Record<string, unknown> = {};

  /**
   * object that will be passed to "filterOptionsFn" as third param
   */
  @Input() filterOptionsPayload: unknown = {};

  /**
   * see "url" and "infiniteScroll" options for more details
   * object that will be sent when loading dropdown options
   */
  @Input() getOptionsParams: Record<string, unknown> = {};

  @Input() getOptionsHeaders?: {
    readonly [header: string]: string | readonly string[] | UUID | readonly UUID[];
  };

  /**
   * prevents caching options list when [url] is set
   */
  @Input() disableCache = true;

  @Input() canSelectEmptyOption = false;
  @Input() emptyOptionLabel = 'option';
  @Input() emptyOptionFullLabel = '';

  /**
   * Can clear input value after selection option, it's needed if we don't link dropdown to ngrxFormControlState
   */

  @Input() canClearInputValue = false;

  /**
   * It needs to recognize which item type user added in case of we have grouped options (groupBy=true),
   */

  @Input() onOptionAddProvideItemName = false;

  /**
   * Show popup with API response errors
   */

  @Input() showApiErrors = false;

  /**
   *  Clear input value after adding new option, usually it's needed if we don't link dropdown to ngrxFormControlState
   */

  @Input() onAddClearInputValue = false;

  /**
   * false - options are loaded automatically when typing
   * true - load options link will be displayed below the input
   */
  @Input() interactiveOptionsLoading = false;

  /**
   * if interactiveOptionsLoading === false
   * this text will be used for loadOptions link
   */
  @Input() loadOptionsLinkText = 'Load options';

  @Input() inputTabIndex?: number;

  @Input() clearInputValueOnFocus = false;

  @Input() highlightChosenOption = true;

  /**
   * see "autocomplete" option for more details
   * emits when filter dropdown options
   */
  @Output() find: EventEmitter<string> = new EventEmitter<string>();

  /**
   * see "canAdd" option for more details
   * emits when adding new option to dropdown list
   */
  @Output() add: EventEmitter<string> = new EventEmitter<string>();

  /**
   * see "canAdd" option for more details
   * emits when adding new option to dropdown list
   */
  @Output()
  addGroup: EventEmitter<AddGroupedOptionPayload> = new EventEmitter<AddGroupedOptionPayload>();

  /**
   * see "canCreate" option for more details
   * emits when new dropdown option created
   */
  @Output() optionCreate: EventEmitter<any> = new EventEmitter<any>();

  /**
   * emits when new value is chosen, emits model
   */
  @Output() valueChange: EventEmitter<any> = new EventEmitter<any>();

  /**
   * emits when new value is chosen, emits chosen option
   */
  @Output() optionChange: EventEmitter<any> = new EventEmitter<any>();

  /**
   * emits when value is cleared
   */
  @Output() optionClean: EventEmitter<any> = new EventEmitter<any>();

  /**
   * emits on dropdown focus when custom template is used
   */
  @Output() inputFocus: EventEmitter<any> = new EventEmitter<any>();

  /**
   * emits when dropdown options loaded by "url"
   */
  @Output()
  optionsLoad: EventEmitter<OptionsLoadEvent> = new EventEmitter<OptionsLoadEvent>();

  /**
   * see "canLoadChosenOption" for more details
   * emits when chosen option is loaded
   */
  @Output() optionLoad: EventEmitter<any> = new EventEmitter<any>();

  /**
   * emits when input is blur
   */
  @Output() inputBlur: EventEmitter<any> = new EventEmitter<any>();

  // @ts-expect-error
  @ViewChild('inputElem') input: ElementRef<HTMLInputElement>;
  // @ts-expect-error
  @ViewChild('listElem') listElem: ElementRef<HTMLElement>;
  // @ts-expect-error
  @ViewChild('scrollElem') scrollElem: ElementRef<HTMLElement>;

  @ContentChildren(DropdownTemplateDirective)
  // @ts-expect-error
  templates: QueryList<DropdownTemplateDirective>;

  // @ts-expect-error
  optionTemplate: TemplateRef<any>;
  // @ts-expect-error
  selectedTemplate: TemplateRef<any>;

  allOptionsLoaded = false;
  searchSubject: BehaviorSubject<string> = new BehaviorSubject<string>('');
  cancelLoadOptionsSubject$ = new Subject<void>();
  searchValue$: Observable<string> = this.searchSubject
    .asObservable()
    .pipe(debounceTime(200));
  loadChosenOptionSubject: Subject<any> = new Subject<any>();

  chosenOption: any;
  chosenOptions: any[] = [];
  chosenByKeyboardOption: any;
  chosenByHoverOption: any;
  showInput = true;
  inputFocused = false;
  loadOptionsOnFocus = false;
  initialized = false;
  uuid = uuid4();

  multipleValue: BehaviorSubject<MultipleValue> = new BehaviorSubject<MultipleValue>({
    amount: 0,
    label: '',
  });
  multipleValue$ = this.multipleValue.asObservable().pipe(debounceTime(100)); // debounce is temporary solution

  groupedOptions: readonly DropdownOptionsGroup[] = [];

  @Input()
  isOptionsLoading = false;

  private lastLoadedOptionsQuery?: string;

  private modelValue: any;
  private prevModelValue: any;
  private allOptions: any[] = [];
  private optionsSet: Array<any[]> = [];

  loadOptionsLinkClick = new Subject();
  showLoadOptionsLink = false;

  @Input() hideSelected = false;

  /**
   * dropdown options trackBy
   */
  @Input() trackBy: (index: number, option: any) => any = (index: number, option: any) =>
    index;

  trackByCreateOptions: (
    index: number,
    option: { readonly url: string; readonly itemName: string },
  ) => string = (_index, { url }) => url;

  @Input()
  shape?: string;

  @Input()
  loadOptionById: undefined | DropdownLoadOptionById;

  @Input()
  mkOption: undefined | DropdownMkOption;

  @Input()
  loadOptionsByUrl: undefined | DropdownLoadOptionsByUrl;

  onChangeCallback = (_: any) => {};

  // @HostListener('document:mousedown')
  documentMouseDown = () => {
    if (!this.inputFocused) {
      return;
    }
    this.inputFocused = false;
    this.cdr.markForCheck();
  };

  ngAfterViewInit() {
    if (this.initialValue !== undefined) {
      if (this.multiple && Array.isArray(this.initialValue)) {
        this.initialValue.forEach(this.selectValue.bind(this));
      } else {
        if (this.valueKey) {
          const valueKey = this.valueKey;
          const option = this.options.find(
            option => option[valueKey] === this.initialValue,
          );
          if (option) {
            this.selectValue(option);
          }
        } else {
          this.selectSingleValue(this.initialValue);
        }
      }
    }
  }

  ngOnInit(): void {
    document.addEventListener('mousedown', this.documentMouseDown);
    if (this.canUseModelAsOption === undefined || this.canUseModelAsOption === null) {
      this.canUseModelAsOption = !this.valueKey;
    }

    if (this.url) {
      this.loadOptions();
    }

    this.searchValue$
      .pipe(
        rx.switchMap(value => {
          if (!this.interactiveOptionsLoading) {
            return of(value);
          } else {
            this.options = [];
            this.cdr.detectChanges();

            return this.loadOptionsLinkClick.pipe(
              mapTo(value),
              tap(_ => {
                this.showLoadOptionsLink = false;
                this.cdr.detectChanges();
              }),
            );
          }
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((value: string) => {
        this.loadOptions(value);
      });
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  ngOnChanges(changes: SimpleChanges) {
    if (this.multiple && changes.initialValue) {
      if (
        !changes.initialValue.currentValue?.length &&
        !changes.initialValue.firstChange
      ) {
        this.clearValue();
        return;
      }

      if (!changes.initialValue.firstChange) {
        this.model = [];
      }

      changes.initialValue.currentValue?.forEach(this.selectMultipleValue.bind(this));
      return;
    }

    if (
      (changes.autocomplete && changes.autocomplete.currentValue) ||
      (changes.url && changes.url.currentValue)
    ) {
      this.filter = true;
    }

    if (changes.multiple && changes.multiple.currentValue) {
      this.showInput = false;
    }

    if (changes.options) {
      this.onOptionsChange();
    }

    if (
      this.url &&
      ((changes.getOptionsParams && !changes.getOptionsParams.firstChange) ||
        (changes.getOptionsHeaders && !changes.getOptionsHeaders.firstChange) ||
        (changes.canAdd && !changes.canAdd.firstChange))
    ) {
      this.loadOptions();
    }

    // eslint-disable-next-line sonarjs/no-collapsible-if
    if (changes.url) {
      if (Array.isArray(this.url)) {
        this.infiniteScroll = false;
      }
    }

    this.cdr.markForCheck();
  }

  ngAfterContentInit(): void {
    this.templates.forEach(dropdownTemplate => {
      if (dropdownTemplate.type === 'option') {
        this.optionTemplate = dropdownTemplate.template;
      }
      if (dropdownTemplate.type === 'selected') {
        this.selectedTemplate = dropdownTemplate.template;
      }
    });

    this.initialized = true;
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  onOptionsChange() {
    this.allOptions = this.options.slice();
    if (this.multiple) {
      if (!this.chosenOptions.length) {
        this.setChosenOptionsByModel(this.model);
      }
      this.setMultipleLabel(this.chosenOptions);
    } else {
      if (!this.chosenOption) {
        this.setChosenOptionByModel(this.model);
      } else if (!this.filter) {
        // update chosenOption in case option properties were changed
        if (this.valueKey) {
          const updatedChosenOption = this.options.find(
            option =>
              option[this.valueKey || ''] === this.chosenOption[this.valueKey || ''],
          );
          if (updatedChosenOption) {
            this.chosenOption = updatedChosenOption;
          }
        }

        this.setLabel(this.chosenOption);
      }
    }
    if (!this.url && this.groupBy) {
      // TODO: change this
      this.groupedOptions = this.getGroupedOptions(this.options);
      /**
       * dropdown options sorting may change after grouping so we need to
       * recreate "this.options" with correct sorting, otherwise choosing
       * dropdown options by keyboard won't work
       */
      this.options = this.groupedOptions.reduce(
        (collectedOptions, group: DropdownOptionsGroup) =>
          // @ts-expect-error
          collectedOptions.concat(group.options),
        [],
      );
    }
  }

  writeValue(value: any) {
    this.prevModelValue = this.modelValue;
    this.modelValue = value;
    if (this.multiple) {
      this.setChosenOptionsByModel(value);
    } else {
      this.setChosenOptionByModel(value);
    }
    if (this.filterOptionsFn) {
      setTimeout((_: any) => this.filterOptions());
    }
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (_: any) => void) {
    this.onChangeCallback = fn;
  }

  registerOnTouched() {}

  focus(e?: any) {
    this.inputFocused = true;
    this.inputFocus.emit(e);
    this.filterOptions();
    if (this.clearInputValueOnFocus) {
      if (this.input && !this.input.nativeElement.value) {
        this.loadOptions();
      } else {
        this.setInputValue('');
        if (this.loadOptionsOnFocus) {
          this.loadOptionsOnFocus = false;
          this.loadOptions();
        }
      }
    }

    if (this.loadOptionsOnFocus && !this.clearInputValueOnFocus) {
      this.loadOptionsOnFocus = false;
      this.loadOptions();
    }

    this.cdr.markForCheck();
  }

  toggleFocus() {
    if (!this.inputFocused) {
      this.resetFilter();
      this.focus();
      setTimeout(() => {
        this.input.nativeElement.focus();
        this.cdr.markForCheck();
      }, 300);
    } else {
      this.resetLabel();
    }
  }

  showInputAndFocus(e: Event) {
    if (this.filter) {
      this.showInput = true;
      setTimeout(() => this.input.nativeElement.focus());
    } else {
      this.inputFocused = true;
      if (this.clearInputValueOnFocus) {
        this.setInputValue('');
      }
    }
  }

  /**
   * determines if whole "this.model" or "this.model[this.getKey()]"
   * should be used for comparing dropdown options
   */
  shouldUseWholeModel(): boolean {
    return !!this.valueKey || !this.compareKey || !this.model;
  }

  /**
   * sets chosen dropdown option by model value in multiple choice mode
   *
   * @param model
   */
  // eslint-disable-next-line sonarjs/cognitive-complexity
  setChosenOptionsByModel(model: any[]) {
    if (this.allOptions && Array.isArray(model)) {
      this.chosenOptions = [];
      const key = this.getKey();
      const useWholeModel = this.shouldUseWholeModel();

      this.allOptions.forEach((option: any) => {
        if (useWholeModel) {
          if (model.indexOf(key ? option[key] : option) !== -1) {
            this.chosenOptions.push(option);
          }
        } else {
          const modelObj = model.find(item => option[key] === item[key]);
          if (modelObj) {
            this.chosenOptions.push(option);
          }
        }
      });
      if (this.input && this.input.nativeElement !== document.activeElement) {
        this.setMultipleLabel(this.chosenOptions.length ? this.chosenOptions : model);
      }
    }
  }

  /**
   * sets chosen dropdown option by model value in single choice mode
   *
   * @param model
   */
  // eslint-disable-next-line sonarjs/cognitive-complexity
  setChosenOptionByModel(model: { [x: string]: any }) {
    if (this.allOptions.length) {
      const key = this.getKey();
      const useWholeModel = this.shouldUseWholeModel();
      this.chosenOption =
        !useWholeModel && !model
          ? undefined
          : this.allOptions.find(
              (option: any) =>
                (key ? option[key] : option) === (useWholeModel ? model : model[key]),
            );
      if (!this.chosenOption && this.canUseModelAsOption) {
        this.chosenOption = model;
      }
      if (
        this.input &&
        this.input.nativeElement !== document.activeElement &&
        this.canResetLabel()
      ) {
        this.setLabel(this.chosenOption);
      }
    }

    if (this.canLoadChosenOption && this.url && !Array.isArray(this.url)) {
      this.loadChosenOptionSubject.next(this.model);
    }
  }

  selectValueAndBlur(option: any) {
    this.selectValue(option);
    this.input.nativeElement.blur();
    this.resetLabel();
  }

  selectValue(option: any): void {
    if (this.multiple) {
      this.selectMultipleValue(option);
      this.optionChange.emit(this.chosenOptions.length ? this.chosenOptions : this.model);
    } else {
      this.selectSingleValue(option);
      this.optionChange.emit(option);
    }
    this.valueChange.emit(this.model);
    this.clearInputValue();
  }

  selectSingleValue(option: any) {
    // if (this.canClear && option && this.chosenOption === option) {
    //   this.setInputValue('');
    // } else {
    this.chosenOption = option;
    this.setLabel(option);
    this.model = this.valueKey ? option[this.valueKey] : option;
    // }
  }

  selectMultipleValue(option: any) {
    if (this.isChosen(option)) {
      const key = this.getKey();
      const useWholeModel = this.shouldUseWholeModel();
      // eslint-disable-next-line functional/no-let
      let index: number;

      if (useWholeModel) {
        index = this.model.indexOf(key ? option[key] : option);
      } else {
        index = this.model.findIndex(
          (item: { [x: string]: any }) => option[key] === item[key],
        );
      }
      const model = [...this.model];
      model.splice(index, 1);

      this.model = model;
    } else {
      this.chosenOptions.push(option);
      this.model = [
        ...(this.model || []),
        this.valueKey ? option[this.valueKey] : option,
      ];
    }
    this.setMultipleLabel(this.chosenOptions.length ? this.chosenOptions : this.model);
  }

  resetFilter() {
    if (this.filter && !this.url) {
      this.resetOptions();
    }
    this.setChosenByKeyboard(undefined);
  }

  /**
   * fires when typing in <input>
   *
   * @param value
   */
  onFilter(value: string = '') {
    if (this.interactiveOptionsLoading) {
      this.showLoadOptionsLink = true;
    }
    if (this.url) {
      this.searchSubject.next(value);
    }

    if (this.storeInputValueToModel) {
      this.model = value;
    }

    if (this.autocomplete || this.url) {
      this.find.emit(value);
    } else {
      // eslint-disable-next-line no-param-reassign
      value = value.toLowerCase();
      if (value.length) {
        this.options = this.allOptions.filter((option: any) => {
          const optionValue = this.getOptionLabel(option) || '';
          return optionValue.toLowerCase().indexOf(value) !== -1;
        });
      } else {
        this.resetOptions();
      }
    }
  }

  resetOptions() {
    this.options = this.allOptions.slice();
  }

  resetLabel() {
    this.inputFocused = false;
    if (this.url && this.searchSubject.value && !this.interactiveOptionsLoading) {
      this.searchSubject.next(''); // stop search
      this.loadOptions(); // reload initial options
    }

    if (this.multiple) {
      this.setMultipleLabel(this.chosenOptions.length ? this.chosenOptions : this.model);
      this.showInput = false;
    } else {
      if (this.chosenOption) {
        this.setInputValue(this.getOptionLabel(this.chosenOption));
      } else {
        this.setDefaultLabel();
      }
    }

    this.cdr.markForCheck();
  }

  setDefaultLabel() {
    if (this.showInvalid) {
      const key = this.getKey();
      const useWholeModel = this.shouldUseWholeModel();
      this.setInputValue((useWholeModel ? this.model : this.model[key]) || '');
    } else {
      this.setInputValue('');
    }
  }

  setLabel(option: any) {
    if (option) {
      this.setInputValue(this.getOptionLabel(option));
    } else {
      this.setDefaultLabel();
    }
  }

  setMultipleLabel(options: any[]) {
    if (this.selectedTemplate) {
      return;
    }

    // eslint-disable-next-line functional/no-let
    let label = '';
    // eslint-disable-next-line functional/no-let
    let amount = 0;
    if (options.length === 1) {
      label = this.getOptionLabel(options[0]);
      this.setInputValue(label);
    } else if (options.length === 0) {
      this.setInputValue(
        this.prevModelValue?.length ? '' : this.input?.nativeElement.value || '',
      );
    } else if (options.length === this.allOptions.length) {
      label = 'Everyone';
      this.setInputValue(label);
    } else if (options.length > 1) {
      if (this.joinMultipleLables) {
        label = options.map(this.getOptionLabel.bind(this)).join(', ');
        this.setInputValue(label);
      } else {
        label = 'Multiple';
        amount = options.length;
        this.setInputValue(`${label} (${amount})`);
      }
    } else {
      this.setInputValue(label);
    }

    this.multipleValue.next({
      label,
      amount,
    });
  }

  setInputValue(value: string) {
    if (this.input) {
      this.input.nativeElement.value = value;
    }
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  changeChosenByKeyboard(e: { keyCode: any; preventDefault: () => void }) {
    if (this.options.length) {
      // eslint-disable-next-line functional/no-let
      let index: number;
      switch (e.keyCode) {
        case 40:
          e.preventDefault();
          index = this.options.indexOf(this.chosenByKeyboardOption);
          if (index === this.options.length - 1) {
            index = -1;
          }
          this.setChosenByKeyboard(this.options[++index]);
          this.setScroll(this.getListItemElement(index));
          break;
        case 38:
          e.preventDefault();
          index = this.options.indexOf(this.chosenByKeyboardOption);
          if (index <= 0) {
            index = this.options.length;
          }
          this.setChosenByKeyboard(this.options[--index]);
          this.setScroll(this.getListItemElement(index));
          break;
        case 27:
          this.input.nativeElement.blur();
          this.resetLabel();
          break;
        case 13: // enter
          if (this.chosenByKeyboardOption) {
            e.preventDefault();
            this.selectValueAndBlur(this.chosenByKeyboardOption);
          }
          break;
      }
    }
  }

  getListItemElement(index: number): HTMLElement {
    return this.listElem.nativeElement.querySelectorAll('.dropdown__menu-item')[
      index
    ] as HTMLElement;
  }

  setScroll(element: HTMLElement) {
    this.scrollElem.nativeElement.scrollTop =
      element.offsetTop - this.listElem.nativeElement.offsetTop;
  }

  scrollToTop() {
    if (this.scrollElem) {
      this.scrollElem.nativeElement.scrollTop = 0;
    }
  }

  setChosenByKeyboard(option: any) {
    this.chosenByKeyboardOption = option;
    if (option) {
      this.setLabel(option);
    }
  }

  setChosenByHover(option: any) {
    this.chosenByHoverOption = option;
  }

  isChosenByKeyboard(option: any): boolean {
    return option === this.chosenByKeyboardOption;
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  isChosen(option: any, isEmptyOptionLabel = false): boolean {
    const key = this.getKey();
    const useWholeModel = this.shouldUseWholeModel();
    if (!this.model && !isEmptyOptionLabel) {
      return false;
    }

    if (this.multiple) {
      if (useWholeModel) {
        if (!key) {
          return this.model?.find((item: { uuid: UUID }) => item.uuid === option.uuid);
        }
        return this.model?.indexOf(key ? option[key] : option) !== -1;
      } else {
        const modelObj = this.model.find(
          (item: { [x: string]: any }) => option[key] === item[key],
        );
        return !!modelObj;
      }
    } else {
      return (
        (this.valueKey ? option[this.valueKey] : option) ===
        (useWholeModel ? this.model : this.model && this.model[key])
      );
    }
  }

  getKey(): string {
    return (!this.valueKey && this.compareKey ? this.compareKey : this.valueKey) || '';
  }

  isExists(value: string): boolean {
    // eslint-disable-next-line no-param-reassign
    value = value.toLowerCase().trim();
    return (
      this.options.findIndex(
        option => this.getOptionLabel(option).toLowerCase().trim() === value,
      ) !== -1
    );
  }

  getOptionLabel(option: { [x: string]: any }): string {
    if (this.labelKey && typeof option === 'object') {
      return option[this.labelKey] || '';
    }

    if (this.labelCallback) {
      return this.labelCallback(option) || '';
    }

    return String(option) || '';
  }

  /**
   * fires when "add new ..." row of dropdown list clicked or "enter" pressed
   *
   * @param value
   */
  addNew(value: string, url?: string, itemName?: string) {
    if (!value) {
      return;
    }

    if (!this.onOptionAddProvideItemName) {
      this.add.emit(value);
      if (this.canUseModelAsOption && this.canAddToModel) {
        this.model = value;
      }
    } else {
      // @ts-expect-error
      this.addGroup.emit({ value, itemName });
    }
    if (url) {
      this.loadOptions();
      if (this.canCreate) {
        this.createOption(value, url);
      }
    }

    if (this.onAddClearInputValue) {
      this.resetLabel();
    }
  }

  findOptionByInputValue() {
    const value = this.input.nativeElement.value.trim().toLowerCase();
    return this.getOptions().find(option => this.getOptionLabel(option) === value);
  }

  inputTabKeyDown(event: Event) {
    const option = this.findOptionByInputValue();
    if (option) {
      this.selectValueAndBlur(option);
    }
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  inputPressEnter(event: Event) {
    if (this.isOptionsLoading) {
      event.stopPropagation();
      event.preventDefault();
      return;
    }

    if (!this.chosenByKeyboardOption && this.chosenByHoverOption) {
      this.selectValueAndBlur(this.chosenByHoverOption);
      return;
    }

    const option = this.findOptionByInputValue();
    if (option) {
      this.selectValueAndBlur(option);
    } else if (this._canAdd) {
      const inputValue = this.input.nativeElement.value.trim();
      if (
        !this.chosenByKeyboardOption &&
        !this.isAddNewHidden() &&
        inputValue &&
        !Array.isArray(this.url)
      ) {
        this.addNew(inputValue, this.url || '');
        this.input.nativeElement.blur();
        this.resetLabel();
      }
    }
  }

  /**
   * returns true if "add new ..." row of dropdown list must be hidden
   * see "canAdd" option for more details
   */
  isAddNewHidden(): boolean {
    const input = this.input ? this.input.nativeElement : undefined;
    const lowerCasedValue = input ? input.value.toLowerCase() : undefined;

    return (
      !input ||
      (this.chosenOption && this.getOptionLabel(this.chosenOption) === input.value) ||
      ((this.shouldUseWholeModel() || this.labelKey === this.valueKey) &&
        this.showInvalid &&
        this.model?.toLowerCase() === lowerCasedValue) ||
      (!this.canAddEmpty && !lowerCasedValue) ||
      (!this.canAddOnMatchLabel &&
        (this.isExists(input.value) ||
          this.isOptionsLoading ||
          input.value !== this.lastLoadedOptionsQuery ||
          this.existingLabels.some(label => label.toLowerCase() === lowerCasedValue)))
    );
  }

  modelIsEmpty(): boolean {
    if (this.multiple) {
      return !this.model || !this.model.length;
    }
    return typeof this.model !== 'boolean' && !this.model;
  }

  clearValue() {
    this.model = this.multiple ? [] : this.emptyValue;
    if (this.multiple) {
      this.optionChange.emit(this.chosenOptions);
    } else {
      this.optionChange.emit(this.chosenOption);
    }
    this.valueChange.emit(this.model);
    this.optionClean.emit();
  }

  clearValueIfInputEmpty() {
    if (
      this.canClearModelIfEmptyInput &&
      !this.modelIsEmpty() &&
      !this.input.nativeElement.value
    ) {
      this.clearValue();
    }
  }

  /**
   * loads and prepares dropdown options if "url" option is set
   *
   * @param search
   * @param infiniteScroll
   */
  // eslint-disable-next-line sonarjs/cognitive-complexity
  loadOptions(search = this.searchSubject.value, infiniteScroll = false) {
    if (
      !this.url ||
      !this.initialized ||
      (!this.inputFocused && (!this.model || this.chosenOption))
    ) {
      if (!this.inputFocused) {
        this.loadOptionsOnFocus = true;
      }

      return;
    }

    this.cancelLoadOptionsSubject$.next();

    const loadOptionsByUrl =
      this.loadOptionsByUrl || this.loadOptionsByUrlDefault.bind(this);

    const shape =
      this.valueKey &&
      !this.labelCallback &&
      !this.filterOptionsFn &&
      !this.templates?.length
        ? {
            _shape:
              typeof this.shape === 'string'
                ? this.shape
                : JSON.stringify({
                    data: {
                      [this.valueKey]: {},
                      ...(this.labelKey && { [this.labelKey]: {} }),
                    },
                  }),
          }
        : {};

    // if "url" option is array of DropdownOptionsSource
    if (Array.isArray(this.url)) {
      this.isOptionsLoading = true;
      this.cdr.markForCheck();
      const sources: Observable<any[]>[] = this.url.map(
        (source: DropdownOptionsSource) => {
          const searchBy = source.searchBy || this.searchBy;

          if (this.disableCache) {
            this.invalidateCache();
          }

          return from(
            loadOptionsByUrl({
              url: source.url,
              limit: this.limit,
              orderBy: source.orderBy || this.orderBy,
              params: source.queryParams || this.getOptionsParams,
              search: search || '',
              searchBy,
              shape,
              showApiErrors: this.showApiErrors,
              headers: this.getOptionsHeaders,
            }),
          ) as Observable<any[]>;
        },
      );
      // load options from all sources in parallel
      forkJoin(sources)
        .pipe(takeUntil(this.cancelLoadOptionsSubject$))
        .subscribe((results: Array<any[]>) => {
          this.optionsSet = results;
          this.optionsLoad.emit({
            offset: 0,
            options: results,
          });

          if (!infiniteScroll) {
            this.scrollToTop();
          }

          if (this.groupBy) {
            this.getFilteredOptionsSet(results)
              .pipe(takeUntil(this.cancelLoadOptionsSubject$))
              .subscribe((optionsSet: any[]) => {
                const groups = this.getGroupedOptions(optionsSet);
                // recreate dropdown options array in correct order
                // after grouping for correctly handle keyboard events
                const options = groups.reduce(
                  (collectedOptions, group: DropdownOptionsGroup) =>
                    // @ts-expect-error
                    collectedOptions.concat(group.options),
                  [],
                );
                this.groupedOptions = infiniteScroll
                  ? this.groupedOptions.concat(groups)
                  : groups;
                this.afterLoadOptions(options, infiniteScroll, search);
              });
          } else {
            const options = results.reduce(
              (collectedOptions, innerOptions: any[]) =>
                collectedOptions.concat(innerOptions),
              [],
            );
            this.afterLoadOptions(options, infiniteScroll, search);
          }
        });
    } else {
      const page =
        infiniteScroll && !this.allOptionsLoaded
          ? Math.floor(this.allOptions.length / this.limit) + 1
          : undefined;

      if (this.disableCache) {
        this.invalidateCache();
      }

      this.isOptionsLoading = true;
      this.cdr.markForCheck();
      (
        from(
          loadOptionsByUrl({
            url: this.url,
            limit: this.limit,
            orderBy: this.orderBy,
            search: search || '',
            searchBy: this.searchBy,
            params: this.getOptionsParams,
            page,
            showApiErrors: this.showApiErrors,
            headers: this.getOptionsHeaders,
          }),
        ) as Observable<any[]>
      )
        .pipe(rx.takeUntil(this.cancelLoadOptionsSubject$))
        .subscribe((options: any[]) => {
          this.optionsLoad.emit({
            offset: infiniteScroll ? this.options.length : 0,
            options,
          });

          if (!infiniteScroll) {
            this.scrollToTop();
          }

          if (this.groupBy) {
            this.getFilteredOptions(options)
              .pipe(takeUntil(this.cancelLoadOptionsSubject$))
              .subscribe(filteredOptions => {
                const groups = this.getGroupedOptions(filteredOptions);
                // recreate dropdown options array in correct order
                // after grouping for correctly handle keyboard events
                // eslint-disable-next-line no-param-reassign
                options = groups.reduce(
                  (collectedOptions, group: DropdownOptionsGroup) =>
                    // @ts-expect-error
                    collectedOptions.concat(group.options),
                  [],
                );
                if (infiniteScroll) {
                  const mergedGroups = [...this.groupedOptions];
                  groups.forEach(group => {
                    const groupIndex = mergedGroups.findIndex(
                      g => g.title === group.title,
                    );
                    if (groupIndex > -1) {
                      mergedGroups[groupIndex] = {
                        title: mergedGroups[groupIndex].title,
                        options: mergedGroups[groupIndex].options.concat(group.options),
                      };
                    } else {
                      mergedGroups.push(group);
                    }
                  });
                  this.groupedOptions = mergedGroups;
                } else {
                  this.groupedOptions = groups;
                }
                this.afterLoadOptions(options, infiniteScroll, search);
              });
          } else {
            this.afterLoadOptions(options, infiniteScroll, search);
          }
        });
    }
  }

  async loadOptionsByUrlDefault({
    url,
    limit,
    orderBy,
    params,
    search,
    searchBy,
    shape,
    page,
    showApiErrors,
    headers,
  }: {
    readonly url: string;
    readonly limit: number;
    readonly orderBy: string;
    readonly params: RR.ReadonlyRecord<string, unknown>;
    readonly searchBy: string;
    readonly search: string;
    readonly page?: number;
    readonly shape?: RR.ReadonlyRecord<string, unknown>;
    readonly showApiErrors: boolean;
    readonly headers?: {
      readonly [header: string]: string | readonly string[] | UUID | readonly UUID[];
    };
  }): Promise<readonly unknown[]> {
    const queryParams: Record<string, any> = {
      ...params,
      limit,
      [`sort_by(${orderBy})`]: 'asc',
    };

    if (page) {
      queryParams.page = page;
    }
    if (shape) {
      queryParams._shape = shape;
    }
    if (search && searchBy) {
      queryParams[searchBy] = search;
    }

    return await firstValueFrom(this.dropdownApiService
      .getList(
        `${environment.api_url}/${addQueryParamsToUrl(url, queryParams)}`,
        url,
        headers,
      )
      .pipe(
        take(1),
        map((resp: APIResponse<any[]>) => resp.data),
        showApiErrors ? this.errorHelperService.catchRequestError() : tap(),
        rx.takeUntil(this.cancelLoadOptionsSubject$),
      ));
  }

  private afterLoadOptions(options: any[], infiniteScroll: boolean, query?: string) {
    this.options = infiniteScroll
      ? this.removeDuplicates(this.allOptions.concat(options))
      : options;
    this.allOptionsLoaded = options.length < this.limit;
    this.onOptionsChange();

    if (!this.groupBy) {
      this.filterOptions();
    }

    this.isOptionsLoading = false;
    this.lastLoadedOptionsQuery = query;
    this.cdr.markForCheck();
  }

  /**
   * checks if chosen dropdown option must be loaded
   * see "canLoadChosenOption" option for more details
   */
  checkAndLoadChosenOption() {
    if (
      // only single option mode for now
      !this.multiple &&
      this.valueKey &&
      !this.modelIsEmpty() &&
      (this.chosenOption === null || typeof this.chosenOption === 'undefined')
    ) {
      this.loadChosenOption();
    }
  }

  /**
   * loads chosen option by id (model must contain entity id)
   * see "canLoadChosenOption" option for more details
   */
  async loadChosenOption() {
    const modelIsPositiveNumber = typeof this.model === 'number' && this.model > 0;
    if (!modelIsPositiveNumber && typeof this.model !== 'string') {
      return;
    }

    const id =
      typeof this.chosenOption === 'object' ? this.idKey(this.chosenOption) : this.model;

    const loadOptionById = this.loadOptionById || this.loadOptionbyIdDefault.bind(this);

    const option = await loadOptionById({ url: this.url as string, id });

    this.chosenOption = option;
    this.setLabel(option);
    this.optionLoad.emit(option);
  }

  async loadOptionbyIdDefault({
    url,
    id,
  }: {
    readonly id: string;
    url: string;
  }): Promise<unknown> {
    const a = this.getLocation(url);

    return await firstValueFrom(this.http
      .get<APIResponse<unknown>>(`${environment.api_url}${a.pathname}/${id}`)
      .pipe(
        map(resp => (resp.data ? resp.data : resp)),
        takeUntil(this.destroy$),
        catchError(error => [null]),
      ));
  }

  moreOptions() {
    if (!this.allOptionsLoaded) {
      this.loadOptions(this.searchSubject.value, true);
    }
  }

  /**
   * loads more options if needed
   * see "autoLoadMoreThreshold" option for more details
   */
  checkAndLoadMoreOptions() {
    if (
      this.infiniteScroll &&
      !this.allOptionsLoaded &&
      this.options.length <= this.autoLoadMoreThreshold
    ) {
      this.moreOptions();
    }
  }

  /**
   * creates new dropdown option
   * see "canCreate" option for more detai  *
   *
   * @param value
   */
  async createOption(value: string, url: string) {
    const createOption = this.mkOption || this.mkOptionDefault.bind(this);

    const option = await createOption({
      value,
      url,
      createOptionBy: this.createOptionBy,
      payload: this.createOptionPayload,
    });
    this.resetSearch();
    this.addOption(option);
    this.selectValue(option);
    this.optionCreate.emit(option);
  }

  async mkOptionDefault({
    value,
    url,
    payload,
    createOptionBy,
  }: {
    value: string;
    url: string;
    payload: RR.ReadonlyRecord<string, unknown>;
    createOptionBy: string;
  }): Promise<unknown> {
    const a = this.getLocation(url);
    return await firstValueFrom(this.http
      .post<APIResponse<unknown>>(`${environment.api_url}${a.pathname}`, {
        ...payload,
        [createOptionBy]: value,
      })
      .pipe(
        map(resp => resp.data),
        takeUntil(this.destroy$),
      ));
  }

  getLocation(href: string): HTMLAnchorElement {
    const l = document.createElement('a');
    l.href = href.startsWith('/') ? href : '/' + href;
    return l;
  }

  addOption(option: any) {
    this.options = this.options.concat(option);
    this.allOptions = this.allOptions.concat(option);

    this.invalidateCache();
  }

  filterOptions() {
    if (this.groupBy) {
      const isMultipleSource = Array.isArray(this.url);
      (isMultipleSource
        ? this.getFilteredOptionsSet(this.optionsSet)
        : this.getFilteredOptions(this.allOptions)
      )
        .pipe(takeUntil(this.destroy$))
        .subscribe(optionsSetOrOptions => {
          this.groupedOptions = this.getGroupedOptions(optionsSetOrOptions);
          this.options = this.groupedOptions.reduce(
            (collectedOptions, group: DropdownOptionsGroup) =>
              // @ts-expect-error
              collectedOptions.concat(group.options),
            [],
          );
          this.cdr.markForCheck();
        });
    } else {
      this.getFilteredOptions(this.allOptions)
        .pipe(takeUntil(this.destroy$))
        .subscribe((options: any[]) => {
          this.options = options;
          this.checkAndLoadMoreOptions();
          this.cdr.markForCheck();
        });
    }
  }

  /**
   * calls external filter function if defined
   *
   * @param options
   */
  getFilteredOptions(options: any[]): Observable<any[]> {
    if (this.hideSelected) {
      // eslint-disable-next-line no-param-reassign
      options = this.multiple
        ? options.filter(option => !this.chosenOptions.includes(option))
        : options.filter(option => this.chosenOption !== option);
    }

    if (typeof this.filterOptionsFn === 'function') {
      const result = this.filterOptionsFn(
        options,
        this.getOptionsParams,
        this.filterOptionsPayload,
        this,
      );
      return isObservable(result) ? result.pipe(take(1)) : of(result);
    } else {
      return of(options);
    }
  }

  getFilteredOptionsSet(optionsSet: Array<any[]>) {
    return forkJoin(optionsSet.map(options => this.getFilteredOptions(options)));
  }

  /**
   * groups dropdown options or dropdown options set (if multiple sources used)
   *
   * @param optionsOrOptionsSet
   */
  // eslint-disable-next-line sonarjs/cognitive-complexity
  getGroupedOptions(
    optionsOrOptionsSet: readonly any[],
  ): readonly DropdownOptionsGroup[] {
    if (!this.groupBy) {
      // @ts-expect-error
      return;
    }

    const groupBySources = this.groupBy === true;
    const isMultipleSource = Array.isArray(this.url);
    if (groupBySources && !isMultipleSource) {
      throw new Error('Cannot group options by source. Multiple sources were not found');
    }

    const result: { [groupId: string]: DropdownOptionsGroup } = {};
    if (groupBySources) {
      return optionsOrOptionsSet.reduce(
        (list: DropdownOptionsGroup[], innerOptions: any[], i: number) =>
          list.concat({
            options: innerOptions,
            title: (this.url as DropdownOptionsSource[])[i].title || 'No title',
          }),
        [],
      );
    } else {
      const options = isMultipleSource
        ? optionsOrOptionsSet.reduce(
            (collectedOptions, innerOptions: any[]) =>
              collectedOptions.concat(innerOptions),
            [],
          )
        : optionsOrOptionsSet;

      options.forEach((option: any) => {
        const title =
          (this.groupByTitleKey &&
            this.getOptionsGroupValueByKey(this.groupByTitleKey, option)) ||
          (this.groupByTitleCallback && this.groupByTitleCallback(option)) ||
          this.emptyGroupTitle;
        const groupId =
          this.getOptionsGroupValueByKey(this.groupBy as string, option) || title || 0;
        if (!result[groupId]) {
          result[groupId] = {
            title,
            options: [],
          };
        }
        result[groupId].options.push(option);
      });
    }

    return Object.values(result);
  }

  /**
   * get dropdown option value by nested key
   *
   * @param key
   * @param option
   */
  getOptionsGroupValueByKey(key: string, option: any) {
    const keyParts = key.split('.');
    return keyParts.reduce((data, keyPart) => (data ? data[keyPart] : data), option);
  }

  /**
   * determines when label (input value) can be set by chosen dropdown option
   */
  canResetLabel(): boolean {
    return !this.url || !this.searchSubject.value;
  }

  resetSearch() {
    this.searchSubject.next('');
  }

  /**
   * invalidates loaded dropdown options cache
   */
  invalidateCache() {
    this.dropdownApiService.invalidateCache(
      Array.isArray(this.url)
        ? (this.url as DropdownOptionsSource[]).map(source => source.url)
        : [this.url || ''],
    );
  }

  getEmptyOption(): any {
    return this.labelKey && this.valueKey
      ? {
          [this.labelKey]: this.getEmptyOptionLabel(),
          [this.valueKey]: this.emptyValue,
        }
      : this.emptyValue;
  }

  getEmptyOptionLabel() {
    if (this.emptyOptionFullLabel) {
      return this.emptyOptionFullLabel;
    } else {
      return `No ${this.emptyOptionLabel} selected`;
    }
  }

  clearInputValue() {
    if (this.input && this.canClearInputValue) {
      this.input.nativeElement.value = '';
    }
  }

  removeDuplicates(options: any[]): any[] {
    const unique: { [key: string]: any } = {};
    const key = this.valueKey || this.compareKey;
    if (!key) {
      return options.filter((option, index, self) => self.indexOf(option) === index);
    }
    options.forEach(option => {
      if (!unique[option[key]]) {
        unique[option[key]] = option;
      }
    });
    return Object.values(unique);
  }

  blur(event: any) {
    this.inputBlur.emit(event);
    this.clearValueIfInputEmpty();
    this.resetLabel();
  }

  getOptions() {
    return this.hideSelected
      ? this.options.filter(option => !this.isChosen(option))
      : this.options;
  }

  getCreateOptions(): { readonly url: string; readonly itemName: string }[] {
    if (Array.isArray(this.url)) {
      return this.url.map(url => ({
        url: url.url,
        itemName: url.itemName || '',
      }));
    } else {
      return [
        {
          url: this.url || '',
          itemName: this.itemName,
        },
      ];
    }
  }

  get _canAdd() {
    return (
      this.inputFocused &&
      (typeof this.canAdd === 'function'
        ? this.canAdd(this.input?.nativeElement.value)
        : Array.isArray(this.canAdd)
        ? this.canAdd.includes(this.input?.nativeElement.value || '')
        : !!this.canAdd)
    );
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.cancelLoadOptionsSubject$.next();
    this.cancelLoadOptionsSubject$.complete();
    document.removeEventListener('mousedown', this.documentMouseDown);
  }

  setAlwaysActive(value: boolean) {
    this.alwaysActive = value;
    this.cdr.markForCheck();
  }
}
